import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, ImagePlus, Briefcase, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { resolveAssetUrl } from '../lib/assets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePortfolio() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);
  const fileRef = useRef(null);

  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', image_url: '', category: '' });

  const fetchItems = useCallback(async () => {
    if (!user?._id) return;
    try {
      const { data } = await axios.get(`${API}/portfolio/${user._id}`);
      setItems(data);
    } catch (e) { /* ignore */ }
  }, [user]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/upload`, fd, { withCredentials: true });
      setForm((f) => ({ ...f, image_url: data.url }));
    } catch (err) {
      toast.error(tr('Augšupielāde neizdevās', 'Upload failed', 'Ошибка загрузки'));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title || form.title.length < 2) {
      toast.error(tr('Ievadi nosaukumu', 'Enter a title', 'Введите название'));
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/portfolio`, form, { withCredentials: true });
      toast.success(tr('Pievienots portfolio!', 'Added to portfolio!', 'Добавлено!'));
      setForm({ title: '', description: '', image_url: '', category: '' });
      setAdding(false);
      fetchItems();
    } catch (err) {
      toast.error(tr('Neizdevās saglabāt', 'Failed to save', 'Ошибка'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/portfolio/${id}`, { withCredentials: true });
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      toast.error(tr('Neizdevās dzēst', 'Failed to delete', 'Ошибка'));
    }
  };

  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 sm:p-8" data-testid="profile-portfolio">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-outfit text-xl font-semibold text-[#121212] flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-[#D97736]" />
          {tr('Pro profils — Portfolio', 'Pro profile — Portfolio', 'Pro-профиль — Портфолио')}
        </h2>
        {!adding && (
          <Button size="sm" onClick={() => setAdding(true)} className="bg-[#D97736] hover:bg-[#C06528] text-white" data-testid="add-portfolio-btn">
            <Plus className="w-4 h-4 mr-1" /> {tr('Pievienot', 'Add', 'Добавить')}
          </Button>
        )}
      </div>
      <p className="text-sm text-[#5C5C5C] mb-6">
        {tr('Parādi savus labākos darbus, lai veidotu uzticību klientiem.',
            'Show your best work to build client trust.',
            'Покажите свои лучшие работы для доверия клиентов.')}
      </p>

      {adding && (
        <div className="space-y-4 p-4 rounded-xl border border-[#E5E4E0] bg-[#FAFAFA] mb-6" data-testid="portfolio-form">
          <div>
            <Label>{tr('Nosaukums', 'Title', 'Название')} *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder={tr('Piem. Virtuves mēbeles pēc pasūtījuma', 'e.g. Custom kitchen cabinets', 'Напр. Кухня на заказ')}
              data-testid="portfolio-title-input" />
          </div>
          <div>
            <Label>{tr('Apraksts', 'Description', 'Описание')}</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3} data-testid="portfolio-desc-input" />
          </div>
          <div>
            <Label>{tr('Attēls', 'Image', 'Изображение')}</Label>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleUpload} data-testid="portfolio-file-input" />
            {form.image_url ? (
              <div className="relative mt-1 w-40 h-28 rounded-lg overflow-hidden border border-[#E5E4E0]">
                <img src={resolveAssetUrl(form.image_url)} alt="" className="w-full h-full object-cover" />
                <button onClick={() => setForm({ ...form, image_url: '' })}
                  className="absolute top-1 right-1 bg-white/90 rounded-full p-1" data-testid="portfolio-remove-image">
                  <Trash2 className="w-3.5 h-3.5 text-red-600" />
                </button>
              </div>
            ) : (
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="mt-1 border-[#E5E4E0] text-[#5C5C5C]" data-testid="portfolio-upload-btn">
                {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-1" />}
                {tr('Augšupielādēt attēlu', 'Upload image', 'Загрузить')}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving} className="bg-[#D97736] hover:bg-[#C06528] text-white" data-testid="save-portfolio-btn">
              {saving ? tr('Saglabā...', 'Saving...', 'Сохранение...') : tr('Saglabāt', 'Save', 'Сохранить')}
            </Button>
            <Button variant="outline" onClick={() => { setAdding(false); setForm({ title: '', description: '', image_url: '', category: '' }); }} className="border-[#E5E4E0] text-[#5C5C5C]">
              {tr('Atcelt', 'Cancel', 'Отмена')}
            </Button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-center py-8 text-[#5C5C5C] text-sm border border-dashed border-[#E5E4E0] rounded-xl" data-testid="portfolio-empty">
          {tr('Vēl nav darbu. Pievieno pirmo!', 'No work samples yet. Add your first!', 'Пока нет работ. Добавьте первую!')}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="portfolio-grid">
          {items.map((item) => (
            <div key={item.id} className="group relative bg-white border border-[#E5E4E0] rounded-xl overflow-hidden" data-testid={`portfolio-item-${item.id}`}>
              {item.image_url && (
                <div className="aspect-[4/3] bg-[#F3F2EE] overflow-hidden">
                  <img src={resolveAssetUrl(item.image_url)} alt={item.title} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-3">
                <p className="font-medium text-[#121212] text-sm line-clamp-1">{item.title}</p>
                {item.description && <p className="text-xs text-[#5C5C5C] line-clamp-2 mt-1">{item.description}</p>}
              </div>
              <button onClick={() => handleDelete(item.id)}
                className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow-sm transition-colors"
                data-testid={`delete-portfolio-${item.id}`}>
                <Trash2 className="w-4 h-4 text-red-600" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
